import { motion } from 'motion/react';
import { Rocket, ArrowRight, TrendingUp, Users, DollarSign } from 'lucide-react';
import type { VentureContext } from '@/types';
import { lightImpact } from '@/utils/haptics';

interface VentureResultProps {
  context: VentureContext;
  onPlanItOut: () => void;
}

const VentureResult = ({ context, onPlanItOut }: VentureResultProps) => {
  if (!context.action) return null;

  return (
    <div className="mt-4 p-5 rounded-2xl gradient-border shadow-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <Rocket className="w-4 h-4 text-orange-600 dark:text-orange-400" />
        </div>
        <span className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">
          Opportunity Detected
        </span>
      </div>

      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-3">
        {context.action.label}
      </h3>

      {context.action.preview && (
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
          {context.action.preview}
        </p>
      )}

      {/* Mock metrics */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <div className="flex flex-col items-center p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <TrendingUp className="w-4 h-4 text-emerald-500 mb-1" />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">High</span>
          <span className="text-[10px] text-slate-400">Potential</span>
        </div>
        <div className="flex flex-col items-center p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <Users className="w-4 h-4 text-primary-500 mb-1" />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Growing</span>
          <span className="text-[10px] text-slate-400">Market</span>
        </div>
        <div className="flex flex-col items-center p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <DollarSign className="w-4 h-4 text-amber-500 mb-1" />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Low</span>
          <span className="text-[10px] text-slate-400">Competition</span>
        </div>
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={async () => { await lightImpact(); onPlanItOut(); }}
        className="w-full flex items-center justify-center gap-2 py-4 px-5 rounded-xl
                   gradient-bg text-white font-semibold text-sm shadow-glow animate-pulse-glow min-h-12"
      >
        <span>📋 Plan It Out</span>
        <ArrowRight className="w-4 h-4" />
      </motion.button>
    </div>
  );
};

export default VentureResult;
